import * as ApiModels from '@typings/api-models';
import * as DocumentApiModels from '@/tsClient/servers/noodleDocuments/document-api-models';
import removeNullish from '@helpers/removeNullish';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import classes from './UserWorkflowSteps.module.scss';
import Attachment from './Attachment';

type Attachment = Parameters<typeof Attachment>[0]['data'];
type WorkflowStep = {
  stepTaken: {
    message: {
      attachments: Attachment[];
    } | null;
  } | null;
  nextSteps: Array<WorkflowStep>;
};

type Props = {
  canAssignCollaborators: Parameters<typeof Attachment>[0]['canAssignCollaborators'];
  collaborators: Parameters<typeof Attachment>[0]['collaborators'];
  customerId: string;
  userWorkflowId: string;
  creator: Parameters<typeof Attachment>[0]['creator'];
  onChange: Parameters<typeof Attachment>[0]['onChange'];
  setAssignCollaboratorModalOpen: Parameters<typeof Attachment>[0]['setAssignCollaboratorModalOpen'];
  setSelectedAttachment: Parameters<typeof Attachment>[0]['setSelectedAttachment'];
  user: { id: string } | null;
  workflowSteps: WorkflowStep[];
};

// flatten nested next steps
const flattenNestedNextSteps = (steps: WorkflowStep[]): WorkflowStep[] => {
  const flattenedSteps: WorkflowStep[] = [];

  steps.forEach(workflowStep => {
    flattenedSteps.push(workflowStep);
    if (workflowStep.nextSteps) {
      flattenedSteps.push(...flattenNestedNextSteps(workflowStep.nextSteps));
    }
  });

  return flattenedSteps;
};

const Attachments: React.FC<Props> = ({
  canAssignCollaborators,
  collaborators,
  creator,
  customerId,
  userWorkflowId,
  onChange,
  setAssignCollaboratorModalOpen,
  setSelectedAttachment,
  user,
  workflowSteps,
}) => {
  // create an array of all attachments assigned to me
  const attachmentsAssignedToMe = flattenNestedNextSteps(workflowSteps)
    .flatMap(step => step.stepTaken?.message?.attachments)
    .filter(a => a?.assigneeIds?.includes(user?.id || '') && a.canAccess)
    .filter(a => {
      if (a?.referenceType === ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
        return a?.data?.status !== DocumentApiModels.DocumentRequestStatus.SUCCEEDED;
      }
      if (a?.referenceType === ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
        return a?.data?.status !== ApiModels.FormStatus.COMPLETED;
      }
      if (a?.referenceType === ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
        // return a?.data?.invoice?.status !== ApiModels.INVOICE_STATUS.PAID && (user?.id === a?.data?.invoice?.personId ? a.data.invoice.status !== : true);
        return a?.data?.invoice.status !== ApiModels.INVOICE_STATUS.PAID;
      }
      if (a?.referenceType === ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
        return !a?.data?.customTerms.agreedAt;
      }
      if (a?.referenceType === ApiModels.MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
        return !a?.data?.booking.skippedAt;
      }
      return true;
    });

  if (attachmentsAssignedToMe.length === 0) {
    return <BannerStatus status="success" label="All caught up! No pending tasks." />;
  }

  return (
    <div className={classes.attachments}>
      {attachmentsAssignedToMe.length > 0
        && attachmentsAssignedToMe
          .filter(removeNullish)
          .map(a => (
            <Attachment
              key={a.id}
              data={a}
              onChange={onChange}
              creator={creator}
              collaborators={collaborators}
              customerId={customerId}
              userWorkflowId={userWorkflowId}
              setAssignCollaboratorModalOpen={setAssignCollaboratorModalOpen}
              setSelectedAttachment={setSelectedAttachment}
              canAssignCollaborators={canAssignCollaborators}
              userId={user?.id}
            />
          ))}
    </div>
  );
};

export default Attachments;
